import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Spin, Image, Tabs, TabsProps, Form, theme, Button, Tag } from "antd";
import {
  LoadingOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import PracticesService, {
  ClientReviewerUserOptionsType,
  DefaultMissCallAgent,
  IGeneralSettingsInitialValues,
  IPractice,
  PracticeTabsType,
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { adminContext } from "../../Common/PageRoute";
import { PageLayout } from "../../Common/Layout";
import BasicInformation from "./BasicInformation";
import TimezoneAndVAT from "./TimezoneAndVAT";
import EmailSettings from "./EmailSettings";
import PracticeSequence from "./PracticeSequence";
import Features from "./Features";
import GeneralSettings from "./GeneralSettings";
import { PracticeBankAccount } from "./PracticeBankAccount";
import AMLReport from "./AMLReport";
import SubmissionHoldList from "../SubmissionHold/SubmissionHoldList";
import BiometricStats from "./BiometricStats";
import AMLReportService from "../../Services/AMLReportService";
import BiometricStatsService from "../../Services/BiometricStats";
import { useUser } from "../../Context/UserContext";
import { usePractice } from "../../Context/PracticeContext";

const defaultData: IPractice = {
  id: "",
  address: {
    building: "",
    city: "",
    country: "",
    county: "",
    postcode: "",
    street: "",
  },
  params: {
    dueInvoicesTaskDays: 0,
    pageTrackTimeout: 0,
    taskDueDateInDays: 0,
    secureLinkExpiry: 0,
  },
  appFeature: {
    accounts: false,
    aml: false,
    bankStatement: false,
    bookkeeping: false,
    calls: false,
    chats: false,
    clientDataRequest: false,
    corporationTax: false,
    dayEnd: false,
    leadSources: false,
    linkedinLeads: false,
    payroll: false,
    teams: false,
    webForms: false,
    timeSheet: false,
    careOf: false,
    selfAssessment: false,
    dms: false,
    audit: false,
    companiesHouseSync: false,
    practiceBookkeeping: false,
    practiceBookkeepingCompany: undefined,
    other1: false,
    other2: false,
    other3: false,
  },
  appSubFeature: {
    accountManagar: false,
    bankDetails: false,
    actionTab: false,
    addCustomer: false,
    digitalSignatureWithoutAML: false,
    manualTimesheet: false,
    progressiveLeads: false,
    websiteQueriesWithOTP: false,
    withoutDiscount: false,
    clientAutomation: false,
    proofOfEarningFPS: false,
    receiptAttachments: false,
    skipTeamUpdate: false,
    secureLink: false,
    amLforAllOfficers: false,
    firstResponse: false,
    accountant: false,
    mode: false,
    agentCredentialsRemark: false,
    clientBrand: false,
    s455TaxLiability: false,
    scheduleEmail: false,
    resourceSection: false,
    allowTLandAdminToUpdateTaskDeadline: false,
    directorAccountWhileRetrieve: false,
    reviewNotificationToTeamleader: false,
    noChangeInTaskAssigneeWithTeamChange: false,
    loeFinancialAutomation: false,
    collectionMethod: false,
    chargeDay: false,
    clientsJoinersAndLeavers: false,
    blockInvoiceEdit: false,
    masking: false,
    xcelify: false,
    clientReferenceInInvoice: false,
    payeMasterReport: false,
    accaBotChat: false,
    preparationSideValidation: false,
    cardNet: false,
    teamSalesComparison: false,
    creditNoteReviewRequest: false,
    reviewAutoRefund: false,
    biometric: false,
    reviewRequestAttachment: false,
    accaBot: false,
    autoComplete: false,
    emailSummarizer: false,
    bookOcrText: false,
    callTranscription: false,
    teamGroup: false,
    teamIsolation: false,
    mtsAutomationSettings: {
      automate: false,
      days: 0,
    },
    noReply: {
      enabled: false,
      replyEmail: "",
    },
    reviewRequestConfig: {
      APCT: false,
      SA100: false,
      SA800: false,
      VAT: false,
    },
  },
  bankAccount: {
    accountName: "",
    accountNumber: "",
    bankName: "",
    bik: "",
    iban: "",
    shortCode: "",
  },
  website: {
    iconUrl: "",
    logoUrl: "",
    url: "",
    urlPrivacy: "",
    urlTnC: "",
  },
  name: "",
  email: "",
  phoneNumber: 0,
  timeZone: "",
  vat: {
    enabled: false,
    rate: 0,
  },
  emailProvider: 0,
  defaultBCCs: [],
  defaultCCs: [],
  sequences: {
    invoice: {
      leadingZero: 0,
      postfix: "",
      prefix: "",
      seperator: "",
      type: 0,
    },
    quote: {
      leadingZero: 0,
      postfix: "",
      prefix: "",
      seperator: "",
      type: 0,
    },
    creditNote: {
      leadingZero: 0,
      postfix: "",
      prefix: "",
      seperator: "",
      type: 0,
    },
    receipt: {
      leadingZero: 0,
      postfix: "",
      prefix: "",
      seperator: "",
      type: 0,
    },
    refund: {
      leadingZero: 0,
      postfix: "",
      prefix: "",
      seperator: "",
      type: 0,
    },
  },
  badges: {
    warningMessage: "",
    dateFrom: "",
    dateTo: "",
    sA100: false,
    sA800: false,
    cis: false,
    vat: false,
    eps: false,
    fps: false,
    confirmationStatement: false,
    corporationTax: false,
    accountsReport: false,
  },
};

interface PracticeDetailsProps {
  data?: any;
}

const PracticeDetails: React.FC<PracticeDetailsProps> = ({
  data,
}: PracticeDetailsProps) => {
  const { openNotification } = React.useContext(adminContext);
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const practiceId = Number(id);
  const { allPracticeIds } = usePractice();
  const [loading, setLoading] = useState(false);
  const [practicesData, setPracticesData] = useState<IPractice>(defaultData);
  const [practiceGS, setPracticeGS] = useState<IGeneralSettingsInitialValues>({
    name: "",
    email: "",
    logoUrl: "",
    connectionString: "",
    database: "",
    host: "",
    identifier: "",
  });
  const [activeTab, setActiveTab] = useState<string>(
    PracticeTabsType.generalSettings,
  );
  const [isEdit, setIsEdit] = useState<{ type: PracticeUpdateTypes }>({
    type: PracticeUpdateTypes.unknown,
  });
  const [refreshBasicInfo, setRefreshBasicInfo] = useState(0);
  const [amlCount, setAmlCount] = useState<number | null>(null);
  const [biometricCount, setBiometricCount] = useState<number | null>(null);

  useEffect(() => {
    setActiveTab(PracticeTabsType.generalSettings);
    setPracticesData(defaultData);
    form.resetFields();
    setAmlCount(null);
    setBiometricCount(null);
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatsCounts = async () => {
    if (!practiceId) return;
    try {
      const [amlRes, bioRes] = await Promise.allSettled([
        AMLReportService.getAMLReport(practiceId, 0, 1, "", "", "", null, null),
        BiometricStatsService.getBiometricStats(
          practiceId,
          0,
          1,
          "",
          undefined,
          undefined,
          undefined,
          undefined,
        ),
      ]);
      if (
        amlRes.status === "fulfilled" &&
        amlRes.value?.status &&
        amlRes.value?.result
      ) {
        setAmlCount(
          amlRes.value.result.totalCompletedCount ??
            amlRes.value.result.totalRecords ??
            0,
        );
      }
      if (
        bioRes.status === "fulfilled" &&
        bioRes.value?.status &&
        bioRes.value?.result
      ) {
        setBiometricCount(
          bioRes.value.result.totalCompletedCount ??
            bioRes.value.result.totalRecords ??
            0,
        );
      }
    } catch {
      // ignore
    }
  };

  const fetchGeneralSettings = async () => {
    try {
      setLoading(true);
      const [gsRes, basicRes] = await Promise.all([
        PracticesService.getPracticeGeneralSettings(practiceId),
        PracticesService.getPracticeBasicInformation(practiceId),
      ]);
      if (gsRes.status && gsRes?.result) {
        setPracticeGS(gsRes?.result);
      }
      if (basicRes?.status && basicRes?.result) {
        setPracticesData((prev) => ({
          ...prev,
          ...basicRes?.result,
          website: basicRes?.result?.website || prev.website,
        }));
      }
    } catch {
      openNotification("error", "Failed to fetch practice details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (practiceId) {
      fetchGeneralSettings();
      fetchStatsCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  useEffect(() => {
    if (practicesData && Object.keys(practicesData).length > 0) {
      form.setFieldsValue(practicesData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practicesData]);

  const handleValuesChange = async (_: any, allValues: any) => {
    setPracticesData(allValues);
  };

  const currentPracticeLabel =
    allPracticeIds.find((o) => o.value === practiceId)?.label ||
    `Practice - ${practiceId}`;

  const logoUrl =
    practiceGS?.logoUrl ||
    practicesData?.website?.logoUrl ||
    practicesData?.website?.iconUrl;

  const items: TabsProps["items"] = [
    {
      key: PracticeTabsType.generalSettings,
      label: "Basic Details",
      children: (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <GeneralSettings
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            setLoading={setLoading}
            generalSettingsData={practiceGS}
            practiceId={practiceId}
            setPracticeGS={setPracticeGS}
          />
          <BasicInformation
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            setLoading={setLoading}
            practiceId={practiceId}
            refreshTrigger={refreshBasicInfo}
            onBasicInfoUpdated={(data) => {
              setPracticesData((prev) => ({
                ...prev,
                ...data,
                website: data?.website || prev.website,
              }));
            }}
          />
          <TimezoneAndVAT
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            setLoading={setLoading}
            practiceId={practiceId}
          />
          <EmailSettings
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            setLoading={setLoading}
            practiceId={practiceId}
          />
        </div>
      ),
    },
    {
      key: PracticeTabsType.bankAccountDetails,
      label: "Bank accounts",
      children: (
        <PracticeBankAccount
          key={practiceId}
          practiceId={practiceId}
          onPrimaryChange={() => setRefreshBasicInfo((prev) => prev + 1)}
        />
      ),
    },
    {
      key: PracticeTabsType.sequenceAndReview,
      label: "Sequence & Review",
      children: (
        <PracticeSequence
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          setLoading={setLoading}
          practiceId={practiceId}
        />
      ),
    },
    {
      key: PracticeTabsType.features,
      label: "Features",
      children: (
        <Features
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          setLoading={setLoading}
          practiceId={practiceId}
        />
      ),
    },
    ...(!isViewOnly
      ? [
          {
            key: PracticeTabsType.submissionHold,
            label: "Submission Hold",
            children: <SubmissionHoldList />,
          },
        ]
      : []),
    {
      key: PracticeTabsType.aml,
      label: "AML",
      children: (
        <AMLReport
          practiceId={practiceId}
          practiceName={currentPracticeLabel}
        />
      ),
    },
    {
      key: PracticeTabsType.biometric,
      label: "AML-Biometric",
      children: (
        <BiometricStats
          practiceId={practiceId}
          practiceName={currentPracticeLabel}
        />
      ),
    },
  ];

  const { token } = theme.useToken();

  const hostUrl = practiceGS?.host
    ? practiceGS.host.startsWith("http://") ||
      practiceGS.host.startsWith("https://")
      ? practiceGS.host
      : `https://${practiceGS.host}`
    : "";

  return (
    <Spin
      spinning={loading}
      indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}
    >
      <Form
        form={form}
        initialValues={practicesData}
        onValuesChange={handleValuesChange}
      >
        <PageLayout>
          {/* Practice Header Card */}
          <div
            style={{
              background: token.colorBgContainer,
              borderRadius: token.borderRadiusLG,
              padding: "16px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 16,
              minHeight: 88,
              boxShadow: "none",
            }}
          >
            {/* Left: Logo & Details */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: token.borderRadiusLG,
                  background: token.colorBgContainer,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  flexShrink: 0,
                  overflow: "hidden",
                  border: `1px solid ${token.colorBorderSecondary}`,
                  padding: 4,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                {logoUrl ? (
                  <Image
                    preview={false}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                    src={logoUrl}
                    alt="logo"
                  />
                ) : (
                  <UserOutlined
                    style={{ fontSize: 32, color: token.colorTextSecondary }}
                  />
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: token.colorText,
                      lineHeight: 1.2,
                    }}
                  >
                    {currentPracticeLabel}
                  </span>
                </div>

                {practiceGS?.host && (
                  <div
                    style={{
                      fontSize: 12,
                      gap: 4,
                      display: "flex",
                      flexWrap: "wrap",
                      color: token.colorTextSecondary,
                    }}
                  >
                    {/* <span>Host: {practiceGS.host}</span> */}
                    <Tag
                      color="blue"
                      bordered={false}
                      style={{
                        margin: 0,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 4,
                      }}
                    >
                      ID: {practiceId}
                    </Tag>

                    {practiceGS?.identifier && (
                      <Tag
                        color="geekblue"
                        bordered={false}
                        style={{
                          margin: 0,
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 4,
                          textTransform: "uppercase",
                        }}
                      >
                        {practiceGS.identifier}
                      </Tag>
                    )}

                    <Tag
                      color="success"
                      bordered={false}
                      style={{
                        margin: 0,
                        fontSize: 12,
                        fontWeight: 500,
                        padding: "2px 8px",
                        borderRadius: 4,
                      }}
                    >
                      Active
                    </Tag>
                  </div>
                )}
              </div>
            </div>

            {/* Right: AML / Biometric Stats & Open Practice App Button */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  background: token.colorFillAlter,
                  borderRadius: token.borderRadiusSM,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  cursor: "pointer",
                }}
                onClick={() => setActiveTab(PracticeTabsType.aml)}
                title="View AML Report"
              >
                <span
                  style={{
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    fontWeight: 500,
                  }}
                >
                  AML:
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: token.colorPrimary,
                  }}
                >
                  {amlCount !== null ? amlCount : "—"}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  background: token.colorFillAlter,
                  borderRadius: token.borderRadiusSM,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  cursor: "pointer",
                }}
                onClick={() => setActiveTab(PracticeTabsType.biometric)}
                title="View AML-Biometric Stats"
              >
                <span
                  style={{
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    fontWeight: 500,
                  }}
                >
                  AML Biometric:
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: token.colorPrimary,
                  }}
                >
                  {biometricCount !== null ? biometricCount : "—"}
                </span>
              </div>

              {hostUrl && (
                <Button
                  type="default"
                  icon={<ExportOutlined />}
                  onClick={() =>
                    window.open(hostUrl, "_blank", "noopener,noreferrer")
                  }
                  style={{
                    borderRadius: token.borderRadiusSM,
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  Open App
                </Button>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <Tabs
              destroyInactiveTabPane={true}
              activeKey={activeTab}
              size="small"
              items={items}
              className="custom-tabs"
              onChange={(key) => setActiveTab(key)}
            />
          </div>
        </PageLayout>
      </Form>
    </Spin>
  );
};

export default PracticeDetails;
